/**
 * Easing curves + standard durations for Data Vault chart transitions.
 *
 * One 4-curve palette every chart interaction picks from. No ad-hoc
 * cubic-beziers floating in chart code — pull from EASINGS instead.
 *
 *   easeOut   — default for hover/fade in/out (Material-style)
 *   spring    — overshoot+settle for "landing" moments (back-out)
 *   decay     — fast start, long tail (inertial settles, momentum)
 *   linear    — only for things that must keep constant velocity
 */

export const EASINGS = {
  easeOut: 'cubic-bezier(0.0, 0.0, 0.2, 1.0)',
  spring: 'cubic-bezier(0.34, 1.56, 0.64, 1.0)',
  decay: 'cubic-bezier(0.16, 1.0, 0.3, 1.0)',
  linear: 'linear',
} as const;

export type EasingName = keyof typeof EASINGS;

/**
 * Standard durations in milliseconds. The whole chart system stays inside
 * the 100–300ms band — anything longer breaks the "Demo Magic ≠ demo
 * theater" rule per CLAUDE.md.
 */
export const DURATIONS = {
  instant: 100,
  fast: 150,
  base: 200,
  slow: 300,
} as const;

export type DurationName = keyof typeof DURATIONS;

/**
 * Shorthand for `transition: <property> <duration>ms <easing>` strings.
 * Useful for inline styles.
 */
export function transition(
  property: string,
  duration: DurationName = 'base',
  easing: EasingName = 'easeOut',
): string {
  return `${property} ${DURATIONS[duration]}ms ${EASINGS[easing]}`;
}
